import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { FileUpload } from 'primereact/fileupload'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { ButtonComponent } from 'Components/UIComponents'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'
import DialogModal from 'Components/UIComponents/DialogModal'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { getLocalJwtToken } from 'Utils/LocalStorageHandler'

const AddCoffeeCastEpisode = ({ setLoading }) => {
  const history = useHistory()
  const { t } = useTranslation()

  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })
  const [breakpoints, setBreakpoints] = useState([
    {
      time: '',
      title: '',
      generated: false
    }
  ])

  const [errors, setErrors] = useState({})
  const [breakpointErrors, setBreakpointErrors] = useState([])
  const [formData, setFormData] = useState({
    channel: '',
    postDescription: ''
  })
  const [uploadStatus, setUploadStatus] = useState()
  const [mediaPost, setMediaPost] = useState()
  const [listOfChannelOwners, setListOfChannelOwners] = useState([])

  const requiredFields = ['channel', 'postDescription']

  const token = getLocalJwtToken()

  useEffect(() => {
    fetchChannelOwners()
  }, [])

  const fetchChannelOwners = async () => {
    try {
      const channelOwnersResponse = await apiAdapterCoffeeWeb.getListOfChannelOwners(token)
      const channelOwner = channelOwnersResponse?.data

      console.log(channelOwner)
      const channelOwnersList = channelOwner?.map((user) => ({
        label: user.channelName,
        value: user.id
      }))

      setListOfChannelOwners(channelOwnersList)
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_LOAD_THE_CHANNEL_OWNERS'),
        onHide: handleDismiss
      })
    }
  }

  const postCoffeeCast = async (payload) => {
    setLoading(true)
    try {
      setLoading(true)

      const response = await apiAdapterCoffeeWeb.postCoffeeCast(token, payload)

      if (response.status === 201) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('DATA_ADDED_SUCCESSFULLY'),
          onHide: handleSuccess,
          onSuccess: handleSuccess
        })
      }
    } catch (error) {
      console.error(error)

      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_ADD_DATA_PLEASE_TRY_AGAIN_LATER'),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms)
    })

  const uploadMediaStatus = async (id, attempt = 0) => {
    const maxAttempts = 90
    const interval = 4000

    if (attempt >= maxAttempts) {
      throw new Error('Processing timed out')
    }

    const response = await apiAdapterCoffeeWeb.getMediaFileUploadStatus(id, token)
    const data = response.data

    if (data.processingStatus === 'COMPLETED') {
      setUploadStatus(data)

      return data
    }

    if (data.processingStatus === 'FAILED') {
      throw new Error(data.processingError || 'Video processing failed')
    }

    await sleep(interval)

    return uploadMediaStatus(id, attempt + 1)
  }

  const uploadFile = async (file) => {
    if (!file) return null
    setShowDialog({ ...showDialog, showModal: false })
    setLoading(true)
    const fileData = new FormData()

    fileData.append('file', file)

    try {
      const response = await apiAdapterCoffeeWeb.postMediaFile(token, fileData)

      setMediaPost(response?.data)

      const mediaId = response.data.id

      await uploadMediaStatus(mediaId)

      return response.data
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_UPLOAD'),
        onHide: handleDismiss,
        onRetry: () => uploadFile(file)
      })
    } finally {
      setLoading(false)
    }
  }

  const validateField = (field, value) => {
    if (!value || value.trim() === '') {
      const formattedField = field?.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str?.toUpperCase())

      return `${formattedField} Is Required`
    }

    return ''
  }

  const validateBreakpointField = (field, value) => {
    if (!value || value.trim() === '') {
      const formattedField = field?.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())

      return `${formattedField} is required`
    }

    return ''
  }

  const handleBlur = (field) => {
    const errorMessage = validateField(field, formData[field])

    setErrors((prev) => ({ ...prev, [field]: errorMessage }))
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const handleFileUpload = async (field, event) => {
    const selectedFile = event.files[0]

    if (!selectedFile) {
      setErrors((prev) => ({ ...prev, [field]: t('IMAGE_IS_REQUIRED') }))

      return
    }
    const uploadedUrl = await uploadFile(selectedFile)

    if (!uploadedUrl) return
    setFormData((prev) => ({ ...prev, [field]: uploadedUrl?.mediaUrl }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const handleBreakpointChange = (index, field, value) => {
    const updated = [...breakpoints]

    updated[index][field] = value
    setBreakpoints(updated)

    const updatedErrors = [...breakpointErrors]

    if (updatedErrors[index]) {
      updatedErrors[index][field] = ''
      setBreakpointErrors(updatedErrors)
    }
  }

  const handleBreakpointBlur = (index, field) => {
    const value = breakpoints[index][field]
    const errorMsg = validateBreakpointField(field, value)
    const updatedErrors = [...breakpointErrors]

    if (!updatedErrors[index]) {
      updatedErrors[index] = { time: '', title: '', timeLineImage: '' }
    }
    updatedErrors[index][field] = errorMsg
    setBreakpointErrors(updatedErrors)
  }

  const handleBreakpointSubmit = async (index) => {
    const bp = breakpoints[index]

    const error = {
      time: validateBreakpointField('time', bp.time),
      title: validateBreakpointField('title', bp.title)
    }

    const updatedErrors = [...breakpointErrors]

    updatedErrors[index] = error
    setBreakpointErrors(updatedErrors)

    if (error.time || error.title) return

    const payload = {
      time: bp.time,
      description: bp.title,
      imageUrl: bp.imageUrl
    }

    try {
      setLoading(true)

      const resp = await apiAdapterCoffeeWeb.postBreakPointsThumbnails(mediaPost?.id, token, payload)

      if (resp.status === 200) {
        setBreakpoints((prev) => {
          const updated = [...prev]

          updated[index] = {
            ...updated[index],
            generated: true,
            time: resp.data?.breakpoints?.[0]?.time,
            title: updated[index].title,
            imageUrl: resp.data?.breakpoints?.[0]?.imageUrl
          }

          updated.push({
            time: '',
            title: '',
            generated: false
          })

          return updated
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const removeBreakPoint = async (index) => {
    try {
      const mediaId = mediaPost?.id
      let timeToDelete

      if (breakpoints[index]?.generated) {
        timeToDelete = breakpoints[index]?.time
      }

      if (!timeToDelete) return

      const deleteBreakPointResponce = await apiAdapterCoffeeWeb.deleteBreakPoint(mediaId, timeToDelete, token)

      if (deleteBreakPointResponce.status === 200) {
        setBreakpoints((prev) => prev.filter((i, breakpointIndex) => breakpointIndex !== index))
        setBreakpointErrors((prev) => prev.filter((i, breakpointIndex) => breakpointIndex !== index))
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_DELETE_BREAKPOINT'),
        onHide: handleDismiss
      })
    }
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const handleSuccess = () => {
    history.push(ROUTE_STRINGS.coffeeCast)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const payload = {
      targetUserId: formData?.channel,
      videoUrl: mediaPost?.mediaUrl,
      content: formData?.postDescription,
      videoDurationSeconds: uploadStatus?.videoDurationSeconds,
      visibility: 'PUBLIC',
      mediaId: mediaPost?.id,
      thumbnailUrl: uploadStatus?.thumbnailUrl,
      breakpoints: breakpoints
        .filter((bp) => bp.generated)
        .map(({ time, title, imageUrl }) => ({
          time,
          description: title,
          imageUrl
        }))
    }

    postCoffeeCast(payload)
  }

  const isMainFormValid = requiredFields.every((field) => formData[field] && formData[field].trim() !== '')
  const areBreakpointsValid = breakpoints.some((bp) => bp.generated) && breakpoints.filter((bp) => bp.time || bp.title).every((bp) => bp.generated)
  const canSubmit = isMainFormValid && areBreakpointsValid

  return (
    <LayoutContainer title={t('ADD_COFFEE_CAST')} breadCrumbsTitle={'addCoffeeCast'}>
      <div className="coffee-cast-episode-add-form">
        <div className="form-fields-row">
          <div className="form-field">
            <span className="p-float-label">
              <Dropdown value={formData.channel} options={listOfChannelOwners} optionLabel="label" optionValue="value" placeholder="Select Channel Owner" filter className={`input-field ${errors.channel ? 'p-error' : ''}`} onChange={(e) => handleInputChange('channel', e.value)} onBlur={() => handleBlur('channel')} appendTo="self" />{' '}
              <label htmlFor="channel" className={`input-label ${errors.channel ? 'error' : ''}`}>
                Channel <span className="required">*</span>
              </label>
            </span>
            {errors.channel && <small className="error-message">{errors.channel}</small>}{' '}
          </div>

          <div className="form-field">
            <span className="p-float-label">
              <InputTextarea className={`input-field ${errors.postDescription ? 'p-error' : ''}`} value={formData.postDescription} onChange={(e) => handleInputChange('postDescription', e.target.value)} onBlur={() => handleBlur('postDescription')} />
              <label className={`input-label ${errors.postDescription ? 'error' : ''}`}>
                {t('VIDEO_DESCRIPTION')} <span className="required">*</span>
              </label>
            </span>
            {errors.postDescription && <small className="error-message">{errors.postDescription}</small>}
          </div>

          <div className="form-field video-upload-field">
            <FileUpload name="videoUrl" mode="basic" accept="video/*" auto chooseLabel={t('UPLOAD_VIDEO')} customUpload onSelect={(event) => handleFileUpload('videoUrl', event)} />
            {formData.videoUrl && (
              <div className="video-preview">
                <video width="100" height="60" controls controlsList="nodownload noplaybackrate" disablePictureInPicture>
                  <source src={formData.videoUrl} type="video/mp4" />
                </video>
              </div>
            )}
            {errors.videoUrl && <small className="error-message">{errors.videoUrl}</small>}
          </div>

          <div className="breakpoints-section">
            <h3>{t('BREAKPOINTS')}</h3>
            {breakpoints?.map((bp, index) => (
              <div key={index} className="breakpoint-row">
                <div className="form-field">
                  <span className="p-float-label">
                    <InputText className={`input-field ${breakpointErrors[index]?.time ? 'p-error' : ''}`} placeholder="HH:MM:SS" value={bp.time} onChange={(e) => handleBreakpointChange(index, 'time', e.target.value)} onBlur={() => handleBreakpointBlur(index, 'time')} />
                    <label className={`input-label ${breakpointErrors[index]?.time ? 'error' : ''}`}>
                      {t('TIME')}
                      <span className="required">*</span>
                    </label>
                  </span>
                  <small className="error-message">{breakpointErrors[index]?.time && <small className="error-message">{breakpointErrors[index]?.time}</small>}</small>
                </div>

                <div className="form-field">
                  <span className="p-float-label">
                    <InputText className={`input-field ${breakpointErrors[index]?.title ? 'p-error' : ''}`} placeholder="Enter Discription" value={bp.title} onChange={(e) => handleBreakpointChange(index, 'title', e.target.value)} onBlur={() => handleBreakpointBlur(index, 'title')} />
                    <label className={`input-label ${breakpointErrors[index]?.title ? 'error' : ''}`}>
                      {t('DESCRIPTION')}
                      <span className="required">*</span>
                    </label>
                  </span>
                  <small className="error-message">{breakpointErrors[index]?.title && <small className="error-message">{breakpointErrors[index]?.title}</small>}</small>
                </div>

                <div className="breakpoint-field">
                  <Button type="button" icon="pi  pi-plus " label="Add" className="file-upload-button" disabled={!uploadStatus} onClick={() => handleBreakpointSubmit(index)} />
                  <Button type="button" icon="pi pi-trash" label="Delete" className="p-button-danger" disabled={!uploadStatus} onClick={() => removeBreakPoint(index, bp)} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-submit-button">
          <ButtonComponent type="cancel" onClick={handleSuccess} />
          <ButtonComponent type="submit" onClick={handleSubmit} disabled={!canSubmit} />
        </div>
      </div>

      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </LayoutContainer>
  )
}

export default Loader(AddCoffeeCastEpisode)
